import { CommentsIcon, DownArrowIcon, EnvelopeIcon } from '@sketchnote/icons';
import cn from 'classnames';
import { useCombobox, useMultipleSelection } from 'downshift';
import { NumberSize } from 're-resizable';
import * as React from 'react';

import { Avatar } from '../avatar';
import { Button } from '../button';
import { MoreActionMenu } from '../more-menu';

const { useState, useMemo, useEffect } = React;

export interface InviteUsersProps {
  members?: {
    firstname: string;
    memberId: string;
    type: string;
    avatar?: string;
    email: string;
  }[];
  inviteBtnText?: string;
  accessLevels: { label: string; value: string }[];
  onSubmit?: (arg: {
    items: {
      firstname: string;
      memberId: string;
      type: string;
      email: string;
      avatar?: string;
      isNew?: boolean;
    }[];
    access: { label: string; value: string };
  }) => void;
  hasAccessLevels?: boolean;
  userInviteLimit?: number;
  memberRate?: { time: string; cost: number };
  memberType?: string;
  guestCount?: number;
  isFreeTrial: boolean;
}

export const InviteUsers = ({
  members = [],
  inviteBtnText = 'Invite',
  onSubmit,
  accessLevels,
  hasAccessLevels = true,
  userInviteLimit,
  memberRate,
  memberType = 'guest',
  guestCount,
  isFreeTrial,
}: InviteUsersProps) => {
  //memberType=member specified for invite modal from manage members,
  //memberType- member-shows prorated value msg
  //memberType- guest - shows guest count restrictions depending on plan
  //member type- guest required current org's guestCount
  const [inputValue, setInputValue] = useState('');
  const [access, setAccess] = useState({
    label: accessLevels[0].label,
    value: accessLevels[0].value,
  });

  const [userLimit, setUserLimit] = useState<number>(userInviteLimit ?? Infinity);
  const [invitedGuests, setInvitedGuests] = useState<number>(guestCount ?? 0);
  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
  } = useMultipleSelection<{
    firstname: string;
    memberId: string;
    type: string;
    email: string;
    avatar?: string;
    isNew?: boolean;
  }>({ initialSelectedItems: [] });

  const filteredItems = useMemo(
    () =>
      members.filter(
        (member) =>
          !selectedItems.find((obj) => obj.memberId === member.memberId) &&
          (member.firstname.toLowerCase().startsWith(inputValue.toLowerCase()) ||
            member.email.toLowerCase().startsWith(inputValue.toLowerCase()))
      ),
    [members, inputValue, selectedItems]
  );

  const isValidNewEmail = useMemo(() => {
    return (
      filteredItems.length === 0 &&
      validateEmail(inputValue) &&
      !selectedItems.find((obj) => obj.email === inputValue)
    );
  }, [filteredItems, inputValue, selectedItems]);

  const {
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    inputValue,
    defaultHighlightedIndex: 0, // after selection, highlight the first item.
    selectedItem: null,
    items: filteredItems,
    onStateChange: ({ inputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          inputValue !== undefined &&
            (inputValue[0] === ','
              ? setInputValue(inputValue.slice(1))
              : setInputValue(inputValue));

          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem) {
            setInputValue('');

            addSelectedItem(selectedItem);
          }
          break;
        default:
          break;
      }
    },
  });

  const handleAddNewEmail = () => {
    if (isValidNewEmail && invitedGuests < userLimit) {
      addSelectedItem({
        firstname: inputValue,
        email: inputValue,
        memberId: '',
        type: '',
        avatar: '',
        isNew: true,
      });
      setInvitedGuests(invitedGuests + 1);
      setInputValue('');
      return {
        firstname: inputValue,
        email: inputValue,
        memberId: '',
        type: '',
        avatar: '',
        isNew: true,
      };
    }
  };

  const handleOnInvite = () => {
    const lastInput = handleAddNewEmail();
    {
      lastInput?.email
        ? onSubmit?.({ items: [...selectedItems, lastInput], access })
        : onSubmit?.({ items: selectedItems, access });
    }
  };

  return (
    <div className=" flex flex-col h-full">
      <div className="h-full">
        <div>
          <div
            {...getComboboxProps()}
            className="py-20 px-20 border-b border-gray-100 flex w-full items-center"
          >
            <div className="flex items-center flex-wrap w-full">
              <span className="mr-8 text-sm">To:</span>
              {selectedItems.map((selectedItem, index) => (
                <span
                  key={`selected-item-${index}`}
                  {...getSelectedItemProps({ selectedItem, index })}
                  className="bg-primary-100 mb-3 flex items-center text-primary-500 px-6 py-3 text-xs mr-4"
                >
                  {selectedItem.firstname}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const isGuestPresent = members.find(
                        (member) => selectedItem.memberId === member.memberId
                      );

                      if (!isGuestPresent) setInvitedGuests(invitedGuests - 1);
                      removeSelectedItem(selectedItem);
                    }}
                    className="ml-5"
                  >
                    &#10005;
                  </button>
                </span>
              ))}

              <input
                {...getInputProps(
                  getDropdownProps({
                    onKeyDown: (e) => {
                      if (e.code === 'Enter' || e.code === 'Comma' || e.code === 'Space') {
                        handleAddNewEmail();
                      }
                    },
                  })
                )}
                className="flex-grow"
                placeholder="Search users/emails"
              />
            </div>
            <div className="flex flex-col">
              {hasAccessLevels && (
                <MoreActionMenu
                  icon={
                    <span className="flex items-center text-gray-600 text-sm whitespace-no-wrap">
                      <span>{access.label} </span>
                      <DownArrowIcon wrapperClass="ml-8" />
                    </span>
                  }
                  menuItems={accessLevels}
                  onClickMenuItem={(e: any) => setAccess({ label: e.label, value: e.value })}
                />
              )}
            </div>
          </div>
          {filteredItems.length > 0 && (
            <div className="py-14 px-20 text-xs text-gray-500">
              <label {...getLabelProps()}>Add an organization member</label>
            </div>
          )}
        </div>
        <div className="overflow-y-auto" style={{ height: '50vh' }}>
          {isValidNewEmail ? (
            <button
              className="flex w-full items-center py-16 px-20 bg-gray-100"
              onClick={handleAddNewEmail}
            >
              <EnvelopeIcon wrapperClass="mr-10 text-gray-500" />
              <span>{inputValue}</span>
            </button>
          ) : null}

          <ul {...getMenuProps()}>
            {filteredItems.map((item, index) => (
              <li
                key={`${item}${index}`}
                {...getItemProps({ item, index })}
                className={cn('list-none flex py-16 px-20 cursor-pointer', {
                  'bg-gray-100': highlightedIndex === index,
                })}
              >
                <Avatar src={item.avatar} type="photo" name={item.firstname} />
                <div className="ml-12">
                  <p className="text-sm font-medium">{item.firstname}</p>
                  <p className="text-xs">
                    <span className="font-medium">{item.type}</span> -{' '}
                    <span className="">{item.email}</span>
                  </p>
                </div>
              </li>
            ))}
          </ul>
          {(!filteredItems.length || !members.length) && (
            <p className="py-14 px-20 text-xs text-gray-500">
              Looks like there are no members in your organization yet, or this file is shared with
              all members
            </p>
          )}
        </div>
        <div className="self-end">
          {!isFreeTrial && (
            <div>
              {memberType === 'member' ? (
                <div className="overflow-hidden bg-blue-100 p-12 text-sm font-medium text-blue-500 rounded-lg mx-20 mb-12">
                  Members added now will be billed {memberRate?.time} at ${memberRate?.cost}/mo.
                </div>
              ) : userLimit !== Infinity ? (
                invitedGuests < userLimit ? (
                  <div className=" bg-blue-100 p-12 text-sm font-medium text-blue-500 rounded-lg mx-20 mb-12">
                    You’ve added {invitedGuests}/{userLimit} guests for this organization
                  </div>
                ) : (
                  <div className="bg-red-100 p-12 text-sm font-medium text-red-500 rounded-lg mx-20 mb-12">
                    The current plan allows only 5 guests. Upgrade for more.
                  </div>
                )
              ) : (
                <></>
              )}
            </div>
          )}

          <div className="h-82 w-full border-t p-20 ">
            <button
              className="w-full bg-blue-500 text-white rounded-sm text-center items-center py-13 text-medium"
              onClick={handleOnInvite}
            >
              {inviteBtnText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function validateEmail(emailAddress: string) {
  //eslint-disable-next-line
  var sQtext = '[^\\x0d\\x22\\x5c\\x80-\\xff]';
  const sDtext = '[^\\x0d\\x5b-\\x5d\\x80-\\xff]';
  const sAtom =
    '[^\\x00-\\x20\\x22\\x28\\x29\\x2c\\x2e\\x3a-\\x3c\\x3e\\x40\\x5b-\\x5d\\x7f-\\xff]+';
  const sQuotedPair = '\\x5c[\\x00-\\x7f]';
  const sDomainLiteral = '\\x5b(' + sDtext + '|' + sQuotedPair + ')*\\x5d';
  const sQuotedString = '\\x22(' + sQtext + '|' + sQuotedPair + ')*\\x22';
  const sDomain_ref = sAtom;
  const sSubDomain = '(' + sDomain_ref + '|' + sDomainLiteral + ')';
  const sWord = '(' + sAtom + '|' + sQuotedString + ')';
  const sDomain = sSubDomain + '(\\x2e' + sSubDomain + ')*';
  const sLocalPart = sWord + '(\\x2e' + sWord + ')*';
  const sAddrSpec = sLocalPart + '\\x40' + sDomain; // complete RFC822 email address spec
  const sValidEmail = '^' + sAddrSpec + '$'; // as whole string

  const reValidEmail = new RegExp(sValidEmail);

  return reValidEmail.test(emailAddress);
}
